import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {robustJsonResultClientFetch} from "@vesta/error-handling-remix/fetches/robustJsonResultClientFetch";
import {z as zod} from "zod";

type GeographyApiContext = {
    intellsysBaseUrl: string;
    accessToken: string;
};

const geographyValuesApiDataValidator = zod
    .object({
        pincode: zod.array(zod.string()).optional(),
        city: zod.array(zod.string()).optional(),
        state: zod.array(zod.string()).optional(),
    })
    .passthrough();

const geographyDetailsApiDataValidator = zod.array(zod.record(zod.string(), zod.unknown()));

export type GeographyValuesResult = {
    pincodes: Array<{value: Uuid; label: string}>;
    cities: Array<{value: Uuid; label: string}>;
    states: Array<{value: Uuid; label: string}>;
};

export type GeographyDetailsResult = {
    state: Uuid | null;
    city: Uuid | null;
    /** Geographic zone node id when the API returns it (e.g. sales / journey customer zone). */
    zone: Uuid | null;
    assignedDealer: string | null;
    latitude: number | null;
    longitude: number | null;
};

function dialCodeToAlpha2CountryCode(dialCode: string): string {
    if (dialCode === "+91") {
        return "IN";
    }
    if (dialCode === "+1") {
        return "US";
    }
    if (dialCode === "+971") {
        return "AE";
    }
    return "IN";
}

function toOptions(values: Array<string> | undefined): Array<{value: Uuid; label: string}> {
    return (values ?? [])
        .filter((value) => typeof value === "string" && value.trim() !== "")
        .map((value) => ({value: value as Uuid, label: value}));
}

export async function getGeographyValues(
    args: GeographyApiContext & {
        dialCode: string;
        pincode?: string | null;
        city?: string | null;
        state?: string | null;
    },
): Promise<GeographyValuesResult> {
    const filters: Record<string, string> = {};
    if (typeof args.pincode === "string" && args.pincode.trim() !== "") {
        filters.pincode = args.pincode.trim();
    }
    if (typeof args.city === "string" && args.city.trim() !== "") {
        filters.city = args.city.trim();
    }
    if (typeof args.state === "string" && args.state.trim() !== "") {
        filters.state = args.state.trim();
    }
    const valuesResult = await robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto/ottopilot/geographic-nodes/values/get`,
        {
            method: "POST",
            headers: {
                "content-type": "application/json",
                authorization: `Bearer ${args.accessToken}`,
            },
            body: JSON.stringify({
                countryCode: dialCodeToAlpha2CountryCode(args.dialCode),
                columns: ["pincode", "city", "state"],
                ...(Object.keys(filters).length > 0 ? {filters: filters} : {}),
            }),
        },
        geographyValuesApiDataValidator,
    );
    if (valuesResult.success !== true) {
        throw valuesResult.error;
    }
    const bag = valuesResult.data;

    return {
        pincodes: toOptions(bag.pincode),
        cities: toOptions(bag.city),
        states: toOptions(bag.state),
    };
}

export async function getGeographyDetailsByPincode(
    args: GeographyApiContext & {pincode: string; dialCode: string},
): Promise<GeographyDetailsResult> {
    const detailsResult = await robustJsonResultClientFetch(
        `${args.intellsysBaseUrl}/sub-system/pluto/ottopilot/geographic-nodes/details/get`,
        {
            method: "POST",
            headers: {
                "content-type": "application/json",
                authorization: `Bearer ${args.accessToken}`,
            },
            body: JSON.stringify({
                pincode: args.pincode,
                countryCode: args.dialCode,
            }),
        },
        geographyDetailsApiDataValidator,
    );
    if (detailsResult.success !== true) {
        throw detailsResult.error;
    }
    const bag = detailsResult.data[0] ?? {};

    const zoneRaw = bag.zone ?? bag.customerZone ?? bag.geographicZone;

    const toCoordinate = (raw: unknown): number | null => {
        if (typeof raw === "number" && Number.isFinite(raw)) {
            return raw;
        }
        if (typeof raw === "string") {
            const parsed = Number(raw);
            return Number.isFinite(parsed) ? parsed : null;
        }
        return null;
    };

    return {
        state: typeof bag.state === "string" ? (bag.state as Uuid) : null,
        city: typeof bag.city === "string" ? (bag.city as Uuid) : null,
        zone: typeof zoneRaw === "string" ? (zoneRaw as Uuid) : null,
        assignedDealer:
            typeof bag.assignedDealer === "string"
                ? bag.assignedDealer
                : typeof bag.dealer === "string"
                ? bag.dealer
                : null,
        latitude: toCoordinate(bag.latitude),
        longitude: toCoordinate(bag.longitude),
    };
}
